import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import html2pdf from 'html2pdf.js';

const cleanHtmlToText = (value) =>
  String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<li>/gi, '\n- ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .trim();

const COMMENT_EMOJI_MAP = {
  ':calendar:': '\u{1F4C5}',
  ':round_pushpin:': '\u{1F4CD}',
  ':rotating_light:': '\u{1F6A8}',
  ':dart:': '\u{1F3AF}',
  ':satellite_antenna:': '\u{1F4E1}',
  ':pushpin:': '\u{1F4CC}',
  ':loud_sound:': '\u{1F50A}',
  ':blue_book:': '\u{1F4D8}',
  ':memo:': '\u{1F4DD}',
};

const formatCommentWithEmojis = (value) => {
  const text = cleanHtmlToText(value);
  if (!text) return '';

  return Object.entries(COMMENT_EMOJI_MAP).reduce(
    (formattedText, [shortcode, emoji]) => formattedText.replaceAll(shortcode, emoji),
    text
  );
};

const formatEventTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHours = String(hours % 12 || 12).padStart(2, '0');
  return `${day} ${month} ${year} ${displayHours}:${minutes} ${suffix}`;
};

const toDataUrl = async (url) => {
  if (!url) return '';
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || '');
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to embed image for PDF:', url, error);
    return url;
  }
};

const A4_WIDTH_PX = 794;
const A4_MIN_HEIGHT_PX = 1123;
const DEFAULT_LOGO = '/static/logo-full.png';
const STATIC_PROTOCOL_ICON_FILES = [
  'animal.png',
  'camera_alignment.png',
  'car_count.png',
  'delivery_person.png',
  'dog_of_leash.png',
  'dumpster.png',
  'emergency_vehicles.png',
  'fall.png',
  'fall_detection.png',
  'fence_cutting.png',
  'fence_jumping.png',
  'fire_detection.png',
  'fire_smoke.png',
  'graffiti.jpg',
  'Homeless.png',
  'illegal_dumping.png',
  'insect.png',
  'medical_emergency.png',
  'people_on_the_property.png',
  'plants.png',
  'protocol_violation.png',
  'protocol_violation_residents.png',
  'protocol_violation_workers.png',
  'rain_snow.png',
  'routine_activity.png',
  'smoking.png',
  'tailgating.png',
  'tech_issue.png',
  'tresspassing.png',
  'truck.png',
  'unauthorised_car_door.png',
  'unauthorised_vehicle_detection.png',
  'unauthorized_entry.png',
  'vandalism.png',
  'walking_at_car_lot.png',
  'weapon_detection_4.png',
  'Box_Misalignment_1.png',
  'Forklift_1.png',
  'LPNR.png',
  'Box_Count_1.png',
  'fighting_detection.jpg',
  'behaviour_violation.jpg',
  'theft.jpg',
];

const normalizeIconKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const STATIC_PROTOCOL_ICON_MAP = STATIC_PROTOCOL_ICON_FILES.reduce((acc, fileName) => {
  acc[normalizeIconKey(fileName)] = `/static/${fileName}`;
  return acc;
}, {});

const STATIC_PROTOCOL_ICON_ALIASES = Object.entries({
  'Homeless / Transients': 'Homeless',
  Animal: 'animal',
  Plants: 'plants',
  'Tech issue/video blur': 'tech_issue',
  'Rain/Snow': 'rain_snow',
  'Insects/Light/Noise': 'insect',
  'Daily workers/Maintenance/Routine Activity': 'routine_activity',
  'Dumpster Usage': 'dumpster',
  'People on the property': 'people_on_the_property',
  'Box count': 'Box_Count_1',
  'Box misalignment': 'Box_Misalignment_1',
  'Already Escalated - Previous incident': 'protocol_violation',
  Forklift: 'Forklift_1',
  'Car Count': 'car_count',
  LPNR: 'LPNR',
  Truck: 'truck',
  'Delivery Person/Visitor': 'delivery_person',
  Vandalism: 'vandalism',
  'Unauthorized Door Access': 'unauthorised_car_door',
  'Suspicious Loitering': 'protocol_violation',
  'Fence Tampering': 'fence_cutting',
  'Routine Activity/Maintenance': 'routine_activity',
  'Emergency/Commercial vehicles': 'emergency_vehicles',
  'Fire/Smoke': 'fire_smoke',
  'Unauthorized usage of Dumpster': 'dumpster',
  'Weapons / Arms Detected': 'weapon_detection_4',
  'Protocol Violation': 'protocol_violation',
  'Protocol Violation - Workers': 'protocol_violation_workers',
  'Protocol Violation - Residents': 'protocol_violation_residents',
  'Weapon Detection': 'weapon_detection_4',
  'Unauthorized Lot Traversal': 'walking_at_car_lot',
  'Fighting Detection': 'fighting_detection',
  Graffiti: 'graffiti',
  'Fall Detection': 'fall_detection',
  Smoking: 'smoking',
  'Fire Detection': 'fire_detection',
  Tailgating: 'tailgating',
  'Unauthorized Entries': 'unauthorized_entry',
  Trespassing: 'tresspassing',
  'Illegal Dumping': 'illegal_dumping',
  'Behaviour Violation': 'behaviour_violation',
  'Theft Attempt': 'theft',
  'Unauthorized Vehicle Detection/Parking': 'unauthorised_vehicle_detection',
  'Pet Off Leash': 'dog_of_leash',
  'Fence Jumping/Cutting': 'fence_jumping',
  'Slip/Fall': 'fall',
  Test: 'protocol_violation',
  'Medical Emergency': 'medical_emergency',
  'Camera Misaligned': 'camera_alignment',
}).reduce((acc, [eventType, iconKey]) => {
  acc[normalizeIconKey(eventType)] = normalizeIconKey(iconKey);
  return acc;
}, {});

const getStaticProtocolIcon = (eventType) => {
  const normalizedEventType = normalizeIconKey(eventType);
  const mappedEventType = STATIC_PROTOCOL_ICON_ALIASES[normalizedEventType] || normalizedEventType;
  return STATIC_PROTOCOL_ICON_MAP[mappedEventType] || '';
};

export default function DailyEscalationTemplate({ data, fileName }) {
  const pdfRef = useRef();
  const pdfData = data?.pdf_data;
  const [embeddedLogoUrl, setEmbeddedLogoUrl] = useState('');
  const [embeddedProtocolIcons, setEmbeddedProtocolIcons] = useState({});

  const stats = Array.isArray(pdfData?.stats) ? pdfData.stats : [];
  const events = Array.isArray(pdfData?.events) ? pdfData.events : [];
  const previewLogoUrl = DEFAULT_LOGO;

  useEffect(() => {
    let active = true;

    toDataUrl(DEFAULT_LOGO).then((result) => {
      if (active) setEmbeddedLogoUrl(result);
    });

    Promise.all(
      events.map(async (event) => {
        const key = event.protocol_icon || '';
        if (!key) return [key, ''];
        const value = await toDataUrl(key);
        return [key, value];
      })
    ).then((pairs) => {
      if (!active) return;
      setEmbeddedProtocolIcons(Object.fromEntries(pairs));
    });

    return () => {
      active = false;
    };
  }, [events, pdfData?.logo_url]);

  const normalizedEvents = useMemo(
    () =>   
      events.map((event) => ({
        protocolIcon: getStaticProtocolIcon(event.EventType) || embeddedProtocolIcons[event.protocol_icon] || event.protocol_icon || '',
        escalatedEventType: event.EscalatedEventType || '',
        cameraName: event.Cameraname || '',
        eventTime: formatEventTime(event.EventTime),
        eventNo: event.EventNo || '',
        escalatedUrl: event.EscalatedUrl || '',
        actionTaken: cleanHtmlToText(event.ActionTaken),
        comment: formatCommentWithEmojis(event.Comment),
      })),
    [embeddedProtocolIcons, events]
  );

  if (!pdfData) return null;

  const waitForImages = async (element) => {
    const images = Array.from(element.querySelectorAll('img'));
    await Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete) {
              resolve();
              return;
            }
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
      )
    );
  };

  const downloadPDF = async () => {
    const element = pdfRef.current;
    if (!element) return;

    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch (error) {
        console.error('Font readiness check failed:', error);
      }
    }

    await waitForImages(element);

    const exportContainer = document.createElement('div');
    const clonedElement = element.cloneNode(true);

    exportContainer.style.position = 'fixed';
    exportContainer.style.top = '0';
    exportContainer.style.left = '0';
    exportContainer.style.opacity = '0';
    exportContainer.style.pointerEvents = 'none';
    exportContainer.style.zIndex = '-1';

    clonedElement.style.minHeight = 'auto';
    exportContainer.appendChild(clonedElement);
    document.body.appendChild(exportContainer);

    try {
      await waitForImages(clonedElement);

      await html2pdf()
        .from(clonedElement)
        .set({
          margin: 0,
          filename: fileName || 'Daily_Escalation_Report.pdf',
          enableLinks: true,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: {
            mode: ['css', 'legacy'],
            avoid: ['.event-section'],
          },
        })
        .save();
    } finally {
      document.body.removeChild(exportContainer);
    }
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Button id="daily-escalation-download-trigger" onClick={downloadPDF} variant="contained">
            Download PDF
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'center', pb: 2, bgcolor: '#f5f5f5', p: 2 }}>
        <Box
          ref={pdfRef}
          sx={{
            width: `${A4_WIDTH_PX}px`,
            minHeight: `${A4_MIN_HEIGHT_PX}px`,
            backgroundColor: '#fff',
            color: '#000',
            fontFamily: '"Poppins", Arial, sans-serif',
            fontSize: '12px',
            px: '35px',
            pt: '25px',
            pb: '18px',
            boxSizing: 'border-box',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
            overflow: 'hidden',
          }}
        >
          <style>
            {`
              @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
            `}
          </style>

          <Box sx={{ minHeight: '100%', display: 'block' }}>
            <Box sx={{ textAlign: 'center', mb: '10px' }}>
              {previewLogoUrl ? (
                <Box
                  component="img"
                  src={previewLogoUrl}
                  alt="logo"
                  sx={{ width: '180px', maxWidth: '100%', objectFit: 'contain', display: 'inline-block' }}
                />
              ) : null}
            </Box>

            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '4px', color: '#111827' }}>
              <Box component="span" sx={{ fontWeight: 700 }}>Daily Report:</Box> {pdfData.report_date}
            </Typography>

            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: pdfData.location ? '2px' : '12px' }}>
              {pdfData.client_name}
            </Typography>

            {pdfData.location ? (
              <Typography sx={{ fontSize: '13px', fontWeight: 600, mt: '-6px', mb: '12px' }}>
                {pdfData.location}
              </Typography>
            ) : null}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '18px', gap: '2%' }}>
              {stats.map((stat) => (
                <Box
                  key={stat.title}
                  sx={{
                    width: '32%',
                    background: '#262626',
                    color: '#fff',
                    pt: '10px',
                    pb: '20px',
                    borderRadius: '18px',
                    textAlign: 'center',
                    boxSizing: 'border-box',
                  }}
                >
                  <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{stat.title}</Typography>
                  <Typography sx={{ fontSize: '19px', fontWeight: 700, lineHeight: 1.2 }}>{stat.value}</Typography>
                </Box>
              ))}
            </Box>

            {normalizedEvents.length === 0 ? (
              <Box
                sx={{
                  background: '#f2f2f2',
                  borderRadius: '18px',
                  py: '20px',
                  px: '15px',
                  textAlign: 'center',
                  fontSize: '13px',
                  fontWeight: 600,
                  mb: '20px',
                  pageBreakInside: 'avoid',
                }}
              >
                <Typography sx={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.5 }}>
                  All cameras were actively monitored,
                </Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.5 }}>
                  and we're happy to report no major events occurred today.
                </Typography>
              </Box>
            ) : null}

            {normalizedEvents.map((event, index) => (
              <Box
                key={`${event.eventNo}-${index}`}
                className="event-section"
                sx={{
                  background: '#f2f2f2',
                  borderRadius: '18px',
                  px: '16px',
                  py: '12px',
                  mb: '18px',
                  pageBreakInside: 'avoid',
                }}
              >
                <Box
                  sx={{
                    background: '#000',
                    color: '#fff',
                    display: 'inline-block',
                    px: '14px',
                    py: '6px',
                    borderRadius: '18px',
                    whiteSpace: 'nowrap',
                    mb: '10px',
                    maxWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-block',
                      width: '22px',
                      height: '22px',
                      background: '#fff',
                      borderRadius: '6px',
                      mr: '8px',
                      verticalAlign: 'middle',
                      textAlign: 'center',
                      lineHeight: '22px',
                      flexShrink: 0,
                    }}
                  >
                    {event.protocolIcon ? (
                      <Box
                        component="img"
                        src={event.protocolIcon}
                        alt=""
                        sx={{ width: '25px', height: '25px', display: 'inline-block', verticalAlign: 'middle' }}
                      />
                    ) : null}
                  </Box>

                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      verticalAlign: 'middle',
                      fontSize: '12px',
                      fontWeight: 600,
                      lineHeight: '18px',
                      color: '#fff',
                      mb: '10px',
                      //mt:'4px'
                    }}
                  >
                    {event.escalatedEventType}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '6px', gap: 2 }}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                      {event.cameraName}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', mt: '1px', color: '#5270ff' }}>
                      {event.eventTime}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', mt: '1px' }}>
                      Event No: {event.eventNo}
                    </Typography>
                  </Box>

                  {event.escalatedUrl ? (
                    <Box sx={{ position: 'relative', zIndex: 10, flexShrink: 0 }}>
                      <Box
                        component="a"
                        href={event.escalatedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          background: '#5270ff',
                          color: '#fff',
                          px: '16px',
                          py: 0,
                          p: '0 16px 10px 16px',
                          fontSize: '12px',
                          borderRadius: '10px',
                          fontWeight: 600,
                          textDecoration: 'none',
                          whiteSpace: 'nowrap',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '104px',
                          height: '40px',
                          lineHeight: '40px',
                          boxSizing: 'border-box',
                          border: 'none',
                          mb: '10px',
                          outline: 'none',
                          '&:link, &:visited, &:hover, &:active, &:focus': {
                            color: '#fff',
                            textDecoration: 'none',
                          },
                        }}
                      >
                        View Video
                      </Box>
                    </Box>
                  ) : null}
                </Box>

                {event.actionTaken ? (
                  <Box sx={{ mb: event.comment ? '8px' : 0 }}>
                    <Box
                      component="pre"
                      sx={{
                        fontFamily: '"Poppins", Arial, sans-serif',
                        fontSize: '12px',
                        lineHeight: '14px',
                        m: 0,
                        p: 0,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word',
                        background: 'transparent',
                        border: 'none',
                      }}
                    >
                      {event.actionTaken}
                    </Box>
                  </Box>
                ) : null}

                {event.comment ? (
                  <Box>
                    <Box
                      component="pre"
                      sx={{
                        fontFamily: '"Poppins", Arial, sans-serif',
                        fontSize: '12px',
                        lineHeight: '14px',
                        m: 0,
                        p: 0,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word',
                        background: 'transparent',
                        border: 'none',
                      }}
                    >
                      {event.comment}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            ))}
          </Box>

          <Typography sx={{ textAlign: 'center', fontSize: '11px', mt: '25px' }}>
            {'All Rights Reserved ©'}{pdfData.year} by {pdfData.TYPE}
          </Typography>
        </Box>
      </Box>
    </>
  );
}
